import * as React from 'react'
import { HelpCircle } from 'lucide-react'

import type { LeadQualificationFunnel } from '@sdr-agend/admin-schemas'
import { formatPercentOrDash } from '@/lib/format'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * "Desempenho por Campanha" (2026-07-31, user request — redesign visual pra
 * bater com a referência "PAINEL COMERCIAL — IA SDR") — transpõe
 * `leadQualificationFunnel.byCampaign` (métrica-por-linha × campanha-por-
 * coluna, com uma coluna "Total"), no mesmo espírito da tabela comparativa
 * da referência.
 *
 * Deliberadamente SEM colunas de Vendas/Faturamento/Ticket médio por
 * campanha — `salesIndicator`/`sales` não têm breakdown por campanha hoje
 * (só o agregado do período todo), e inventar essa coluna seria dado que a
 * API não retorna (Article IV). As métricas abaixo são exatamente o que
 * `byCampaign` já expõe.
 *
 * `noShow` (2026-08-04, user request) — reaproveita a mesma contagem já usada
 * pela taxa de comparecimento do funil (`queryLeadQualificationFunnel`'s
 * `byCampaign` query, filtro `lnm.outcome = 'no_show'`).
 */
export interface CampaignComparisonTableProps {
  data: LeadQualificationFunnel
}

interface MetricRow {
  key: string
  label: string
  values: (leadsReceived: number, qualified: number, scheduled: number, noShow: number) => string
}

const ROWS: MetricRow[] = [
  { key: 'leads', label: 'Leads', values: (l) => l.toLocaleString('pt-BR') },
  { key: 'qualified', label: 'Qualificados', values: (_l, q) => q.toLocaleString('pt-BR') },
  { key: 'scheduled', label: 'Agendamentos', values: (_l, _q, s) => s.toLocaleString('pt-BR') },
  {
    key: 'qualToScheduleRate',
    label: 'Taxa Qualif. → Agenda',
    values: (_l, q, s) => formatPercentOrDash(q > 0 ? s / q : null),
  },
  // 2026-08-04 (user request) — indicador de no-show por campanha.
  { key: 'noShow', label: 'No-show', values: (_l, _q, _s, ns) => ns.toLocaleString('pt-BR') },
]

export function CampaignComparisonTable({ data }: CampaignComparisonTableProps): React.ReactElement {
  if (data.byCampaign.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="campaign-comparison-table-empty"
      >
        Nenhum lead recebido por campanha no período selecionado.
      </div>
    )
  }

  const columns = [
    ...data.byCampaign.map((c) => ({
      key: c.campaignId,
      label: c.campaignName,
      leadsReceived: c.leadsReceived,
      qualified: c.qualified,
      scheduled: c.scheduled,
      noShow: c.noShow,
    })),
    // Coluna "Total" — reaproveita os agregados do período já calculados pela
    // rota (mesmos números do funil no topo da página), não uma soma
    // reconstruída aqui.
    //
    // QA fix (2026-08-05 review) — "Agendamentos"/"No-show" no Total podem
    // ser MAIORES que a soma das colunas de campanha ao lado: Total conta
    // qualquer reunião com uma conversa de confirmação vinculada (inclusive
    // reuniões marcadas direto no GHL por um vendedor, sem origem em nenhuma
    // campanha Lead Novo), enquanto cada coluna de campanha só atribui
    // reuniões que de fato começaram como lead daquela campanha. Ver
    // tooltip no cabeçalho "Total" + docstring de `queryLeadQualificationFunnel`.
    {
      key: '__total__',
      label: 'Total',
      leadsReceived: data.leadsReceived,
      qualified: data.qualified,
      scheduled: data.scheduled,
      noShow: data.noShow,
    },
  ]

  return (
    <div className="overflow-x-auto" data-testid="campaign-comparison-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="pb-1.5 pr-3 font-medium">Métrica</th>
            {columns.map((c) => (
              <th
                key={c.key}
                className={
                  c.key === '__total__'
                    ? 'pb-1.5 pl-3 text-right font-semibold text-foreground'
                    : 'pb-1.5 pl-3 text-right font-medium'
                }
              >
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  {c.key === '__total__' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="shrink-0 text-muted-foreground/70 hover:text-foreground"
                          aria-label='O que é "Total"?'
                        >
                          <HelpCircle className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Inclui reuniões marcadas direto no GHL por um vendedor (sem origem em
                        campanha Lead Novo), por isso pode ser maior que a soma das colunas ao
                        lado.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.key} className="border-b last:border-0" data-testid={`campaign-comparison-row-${row.key}`}>
              <td className="py-1.5 pr-3 text-muted-foreground">{row.label}</td>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={
                    c.key === '__total__'
                      ? 'py-1.5 pl-3 text-right font-semibold tabular-nums'
                      : 'py-1.5 pl-3 text-right tabular-nums'
                  }
                >
                  {row.values(c.leadsReceived, c.qualified, c.scheduled, c.noShow)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default CampaignComparisonTable
